import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { BOOKING_URL, IMG, scrollToId } from "../data/content";

const LINES = ["Indian Heritage", "Cultural Centre", "Penang"];

const Hero = () => {
    const { scrollY } = useScroll();
    const y = useTransform(scrollY, [0, 900], [0, 220]);
    const scale = useTransform(scrollY, [0, 900], [1.08, 1.18]);

    return (
        <section
            id="top"
            data-testid="hero-section"
            className="ihcc-grain relative flex min-h-screen flex-col justify-end overflow-hidden bg-heritage-ink"
        >
            <motion.div style={{ y, scale }} className="absolute inset-0">
                <img
                    src={IMG.hero}
                    alt="The IHCC heritage shophouse facade at night on Lebuh Pantai, George Town, Penang"
                    className="h-full w-full object-cover"
                    fetchPriority="high"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-heritage-ink via-heritage-ink/55 to-heritage-ink/35" />
                <div className="absolute inset-0 bg-gradient-to-r from-heritage-ink/70 via-transparent to-transparent" />
            </motion.div>

            <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-14 pt-40 md:px-10 md:pb-20">
                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="mb-8 font-mono text-[11px] uppercase tracking-[0.3em] text-heritage-gold"
                >
                    57 Lebuh Pantai · George Town · Open Daily 10 AM – 7 PM
                </motion.p>

                <h1
                    data-testid="hero-heading"
                    className="font-serif text-[13vw] font-medium leading-[0.98] tracking-tight text-heritage-parchment sm:text-6xl md:text-7xl lg:text-8xl"
                >
                    {LINES.map((line, i) => (
                        <span key={line} className="block overflow-hidden pb-1">
                            <motion.span
                                className={`block ${i === 1 ? "italic text-heritage-gold" : ""}`}
                                initial={{ y: "112%" }}
                                animate={{ y: "0%" }}
                                transition={{
                                    duration: 1,
                                    delay: 0.35 + i * 0.15,
                                    ease: [0.22, 1, 0.36, 1],
                                }}
                            >
                                {line}
                            </motion.span>
                        </span>
                    ))}
                </h1>

                <motion.p
                    data-testid="hero-subtitle"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.95 }}
                    className="mt-7 max-w-xl text-base leading-relaxed text-heritage-parchment/80 md:text-lg"
                >
                    Discover Indian Heritage, Culture &amp; History in the Heart of George Town
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 1.1 }}
                    className="mt-10 flex flex-wrap items-center gap-4"
                >
                    <a
                        href={BOOKING_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="hero-book-tickets-button"
                        className="group inline-flex items-center gap-2 rounded-full bg-heritage-terra px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] text-heritage-parchment transition-all duration-300 hover:scale-[1.03] hover:bg-heritage-vermilion"
                    >
                        Book Tickets
                        <ArrowUpRight
                            size={16}
                            className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                        />
                    </a>
                    <a
                        href="#about"
                        onClick={(e) => {
                            e.preventDefault();
                            scrollToId("#about");
                        }}
                        data-testid="hero-explore-button"
                        className="inline-flex items-center gap-2 rounded-full border border-heritage-parchment/30 px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] text-heritage-parchment/90 transition-all duration-300 hover:border-heritage-gold hover:text-heritage-gold"
                    >
                        Explore IHCC
                    </a>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 1.4 }}
                    className="mt-16 flex flex-wrap items-center gap-x-10 gap-y-3 border-t border-white/10 pt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-heritage-parchment/50"
                >
                    <span>UNESCO Heritage Zone</span>
                    <span>≈ 90-Minute Hosted Journey</span>
                    <span>English · Malay · Chinese · Indonesian</span>
                    <span className="ml-auto hidden items-center gap-2 md:flex">
                        Scroll <ArrowDown size={13} className="animate-bounce" />
                    </span>
                </motion.div>
            </div>
        </section>
    );
};

export default Hero;
